import Sentry from "@similarweb/sentry-logger";

import { resolveQuickSearchCatalogPageUrl } from "app/components/React/QuickSearch/helpers/resolveQuickSearchCatalogPageUrl";
import quickSearchTracking from "app/components/React/QuickSearch/tracking";
import { router } from "single-spa/react-app/router/router";

/**
 * Predicts whether a resolved hash URL maps to a registered router state.
 *
 * When it does not, navigating to that URL silently falls through to UI-Router's `otherwise`
 * rule and redirects the user to a fallback page instead of the intended report — the
 * "click does nothing" the user perceives. Returns `true` when the router is not initialized
 * yet or matching throws, so we never report false navigation failures.
 */
const doesHashMatchState = (hash: string): boolean => {
    if (!router) {
        return true;
    }
    try {
        const withoutHash = hash.startsWith("#") ? hash.slice(1) : hash;
        const [path, queryString = ""] = withoutHash.split("?");
        // Account paths are intentionally handled by the router's otherwise rule,
        // not registered UIRouter states — skip state validation for them.
        if (path.startsWith("/account/")) {
            return true;
        }
        const search = Object.fromEntries(new URLSearchParams(queryString));
        const matchResult = router.urlService.match({ path, search });
        return matchResult?.rule?.type === "STATE";
    } catch {
        return true;
    }
};

/**
 * Navigates within the Pro SPA using a hash URL from the pages catalog (e.g. "#/activation/home").
 * Catalog URLs may contain `{placeholders}`; they are resolved before navigation.
 *
 * Some catalog entries resolve to a hash that matches no registered router state (e.g. an
 * unresolved placeholder or a stale/renamed route). Those navigations fail silently, so we detect
 * them up front and report them to Sentry + tracking, while still performing the navigation.
 */
export const navigateToQuickSearchPageUrl = (url: string, reportTitle?: string): void => {
    const hash = resolveQuickSearchCatalogPageUrl(url);

    if (!doesHashMatchState(hash)) {
        Sentry.errorTracker({
            e: new Error("Quick Search report navigation did not resolve to a valid page"),
            message: "Quick Search report navigation failed",
            level: "error",
            params: {
                extra: {
                    reportTitle: reportTitle ?? "",
                    catalogUrl: url,
                    resolvedHash: hash,
                    currentState: router?.globals?.current?.name,
                },
            },
        });
        quickSearchTracking.trackReportNavigationFailed(reportTitle ?? "", hash);
    }

    const base = window.location.href.split("#")[0];
    window.location.href = base + hash;
};
